import React, { memo, useState } from 'react'
import { css } from 'styled-components/macro'

import { Box } from '@alobato/flex-box'
import Text from '@alobato/text'
import { Table, TableContainer, TableRow, TableCell, TableHeaderRow, TableHeaderCell } from '../../components/DataTable'
import { EmptyState } from '../../components/ui'
import Pagination from '../../components/Pagination'

import MenuOver from '../../components/MenuOver'
import { MoreIcon } from '../../components/Icons'
import Modal from '../../components/StyledModalOverflow'

import Show from './Show'
import Edit from './Edit'

import BASE_API from '../../constants/baseApi'
import AUTH_TOKEN from '../../constants/authToken'

const LIST_URL = `${BASE_API}/solicitation/`

const formatDateTime = text => {
  if (!text) return '—'
  const d = new Date(text)
  if (isNaN(d)) return text
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}


/* A senha do paciente NO LABORATÓRIO, em texto puro.

   É a credencial que o paciente usa no sistema da clínica, e ela precisa
   existir aqui: quem roda a integração loga no laboratório em nome dele. O
   problema era mostrá-la numa LISTA — quem abrisse a tela de solicitações
   enxergava a senha de todos os pacientes de uma vez, e a tela vive aberta em
   máquina compartilhada, aparece em screenshot e em gravação de tela.

   Mascarada por padrão, com revelação uma a uma. O operador continua
   conseguindo trabalhar; o que some é a exposição ambiente. O estado é local
   ao componente, então mudar de página ou recarregar volta tudo a mascarado —
   de propósito.

   Isto NÃO protege o dado no servidor: ele continua em texto no banco e vem
   em texto na resposta da API. Está registrado em tria-api/docs/14-seguranca.md. */
const SenhaMascarada = ({ valor }) => {
  const [visivel, setVisivel] = useState(false)

  if (!valor) return '—'

  if (!visivel) {
    return (
      <Box d='flex' ai='center' css={{ gap: 8 }}>
        <span style={{ letterSpacing: 2, color: '#8a97a8' }}>••••••••</span>
        <button
          type='button'
          onClick={() => setVisivel(true)}
          style={{
            border: 0, background: 'none', padding: 0, cursor: 'pointer',
            color: '#2f9bd6', fontSize: 12, fontWeight: 600
          }}
        >
          revelar
        </button>
      </Box>
    )
  }

  return (
    <Box d='flex' ai='center' css={{ gap: 8 }}>
      <span style={{ fontFamily: 'monospace' }}>{valor}</span>
      <button
        type='button'
        onClick={() => setVisivel(false)}
        style={{
          border: 0, background: 'none', padding: 0, cursor: 'pointer',
          color: '#8a97a8', fontSize: 12, fontWeight: 600
        }}
      >
        ocultar
      </button>
    </Box>
  )
}

const STATUS_COLORS = {
  'PENDENTE': ['#fff4e0', '#a86a12'],
  'CONCLUÍDA': ['#e6f6ec', '#1d7a45'],
  'CONCLUÍDA COM ERRO': ['#fdeaea', '#b3261e'],
  'ERRO': ['#fdeaea', '#b3261e']
}

const StatusBadge = ({ status }) => {
  const [bg, color] = STATUS_COLORS[status] || ['#eef2f7', '#5b6b80']
  return (
    <Box
      d='inline-block'
      css={css`padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; background: ${bg}; color: ${color};`}
    >
      {status || '—'}
    </Box>
  )
}

/* Acessos tolerantes a nulos: em produção há solicitações sem estabelecimento
   vinculado (texto livre), sem cidade e sem credenciais. */
const establishmentOf = item => (item.establishment && item.establishment.name) || item.establishment_name || '—'
const patientEmailOf = item => (item.user && item.user.login) || '—'
const statusOf = item => item.status || ''

const List = ({ data, navigate, path, id, onPageChange, currentPage, pageCount }) => {

  const [items, setItems] = useState(data)

  const getItems = async () => {
    try {
      return await (await fetch(LIST_URL, { headers: { Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}` }})).json()
    } catch(e) {
      console.error(e)
      return []
    }
  }

  const handleMenuOverClick = (action, id) => {
    // Navegação com reload completo: a montagem client-side deste container
    // (useQueryParam + useFetch aninhados) não dispara o fetch do detalhe de
    // forma confiável; com page load o modal funciona 100%.
    switch (action) {
      case 'show':
        window.location.assign(`/solicitacoes/${id}`)
        break
      case 'edit':
        window.location.assign(`/solicitacoes/${id}/editar`)
        break
      default:
        return false
    }
  }

  const completedSolicitations = items => items.filter(item => statusOf(item).includes('CONCLUÍDA'))

  /* Inclui PENDENTE, PENDENTE (crawler) e também ERRO — antes, solicitações
     com status ERRO não apareciam em nenhuma das duas tabelas. */
  const pendingSolicitations = items => items.filter(item => {
    const status = statusOf(item)
    return status.includes('PENDENTE') || status === 'ERRO'
  })

  return (
    <>
      <Box mb={3}>
        <Text medium fw={600}>Pendentes</Text>
      </Box>

      <Box mb={5}>
        {!!pendingSolicitations(items).length ? (
          <TableContainer>
            <Table>
              <TableHeaderRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Data/Hora</TableHeaderCell>
                <TableHeaderCell>Cidade</TableHeaderCell>
                <TableHeaderCell>Estabelecimento</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Login</TableHeaderCell>
                <TableHeaderCell>Senha</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
              </TableHeaderRow>
              {pendingSolicitations(items).map(item => (
                <TableRow key={item.id}>
                  <TableCell data-title='ID'>{item.id}</TableCell>
                  <TableCell data-title='Data/Hora'>{formatDateTime(item.created_at)}</TableCell>
                  <TableCell data-title='Cidade'>{item.city || '—'}</TableCell>
                  <TableCell data-title='Estabelecimento'>{establishmentOf(item)}</TableCell>
                  <TableCell data-title='Email'>{patientEmailOf(item)}</TableCell>
                  <TableCell data-title='Login'>{item.login || '—'}</TableCell>
                  <TableCell data-title='Senha'><SenhaMascarada valor={item.password} /></TableCell>
                  <TableCell data-title='Status'><StatusBadge status={item.status} /></TableCell>
                  <TableCell width={40} lineHeight={0}>
                    <MenuOver
                      options={[{label: 'Detalhes', value: 'show'}, {label: 'Editar', value: 'edit'}]}
                      onSelect={action => handleMenuOverClick(action, item.id)}
                    >
                      <MoreIcon style={{cursor: 'pointer'}} />
                    </MenuOver>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </TableContainer>
        ) : (
          <EmptyState />
        )}
      </Box>
  

      <Box mb={3}>
        <Text medium fw={600}>Concluídas</Text>
      </Box>

      <Box mb={5}>
        {!!completedSolicitations(items).length ? (
          <TableContainer>
            <Table>
              <TableHeaderRow>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>Data/Hora</TableHeaderCell>
                <TableHeaderCell>Cidade</TableHeaderCell>
                <TableHeaderCell>Estabelecimento</TableHeaderCell>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Login</TableHeaderCell>
                <TableHeaderCell>Senha</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell></TableHeaderCell>
              </TableHeaderRow>
              {completedSolicitations(items).map(item => (
                <TableRow key={item.id}>
                  <TableCell data-title='ID'>{item.id}</TableCell>
                  <TableCell data-title='Data/Hora'>{formatDateTime(item.created_at)}</TableCell>
                  <TableCell data-title='Cidade'>{item.city || '—'}</TableCell>
                  <TableCell data-title='Estabelecimento'>{establishmentOf(item)}</TableCell>
                  <TableCell data-title='Email'>{patientEmailOf(item)}</TableCell>
                  <TableCell data-title='Login'>{item.login || '—'}</TableCell>
                  <TableCell data-title='Senha'><SenhaMascarada valor={item.password} /></TableCell>
                  <TableCell data-title='Status'><StatusBadge status={item.status} /></TableCell>
                  <TableCell width={40} lineHeight={0}>
                    <MenuOver
                      options={[{label: 'Detalhes', value: 'show'}, {label: 'Editar', value: 'edit'}]}
                      onSelect={action => handleMenuOverClick(action, item.id)}
                    >
                      <MoreIcon style={{cursor: 'pointer'}} />
                    </MenuOver>
                  </TableCell>
                </TableRow>
              ))}
            </Table>
          </TableContainer>
        ) : (
          <EmptyState />
        )}
      </Box>

      <Box ta='center'>
        <Pagination onPageChange={onPageChange} currentPage={currentPage} pageCount={pageCount} />
      </Box>

      {path === '/:id/editar' && id &&
        <Modal
          clickOutsideDisabled
          onCloseCompleted={() => navigate('../../')}
          render={
            ({onRequestClose}) => (
              <Edit
                id={id}
                onCompleted={async () => {
                  const newItems = await getItems()
                  setItems(newItems.results)
                }}
                onRequestClose={onRequestClose}
              />
            )
          }
        />
      }

      {path === '/:id' && id &&
        <Modal
          clickOutsideDisabled
          onCloseCompleted={() => navigate('../')}
          render={
            ({onRequestClose}) => (
              <Show
                id={id}
                onCompleted={async () => {
                  const newItems = await getItems()
                  setItems(newItems.results)
                }}
                onRequestClose={onRequestClose}
              />
            )
          }
        />
      }
    </>
  )
}

export default memo(List)